import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Organization } from 'src/app/internal-user/customer-management/models/organization';
import { DynamicOption, InputMode, InputType, SystemType, UserInputPropertySource } from '../../models/deploymentForm.model';
import { DeploymentProvisionStateService } from '../../services/deployment-provision-state.service';
import { FleetConfigurationService } from '../../services/fleet-configuration.service';

/**
 * Represents a dynamic control that is driven by server metadata.
 * Creates one of many Material UI controls based on the metadata's input type and binds it to the associated form control.
 */
@Component({
    selector: 'app-dynamic-deployment-control',
    templateUrl: './dynamic-deployment-control.component.html',
    styleUrls: ['./dynamic-deployment-control.component.scss'],
    standalone: false
})
export class DynamicDeploymentControlComponent implements OnInit, OnChanges, OnDestroy {
  @Input() organizationId: string;
  @Input() mode: InputMode;
  @Input() controlMetadata: UserInputPropertySource;
  @Input() form: FormGroup;
  @Input() systemType: SystemType | null | undefined;
  /** Names of all deployment-level properties whose input type is EdgeDeviceDropdown (for generic peer exclusion). */
  @Input() edgeDeviceDropdownNames: string[] = [];
  @Input() selectedOrganization: Organization | null = null;
  @Input() selectedSite: Organization | null = null;
  @Output() siteSelected = new EventEmitter<Organization | null>();

  /** Options fetched from the dynamic options API. */
  dynamicOptions: DynamicOption[] = [];
  /** Whether dynamic options are currently being loaded. */
  dynamicOptionsLoading = false;
  /** Placeholder text shown when dynamic options aren't yet available. */
  dynamicPlaceholder = '';
  /** When re-editing, if the previously saved value is no longer in the fetched options. */
  staleOptionLabel: string | null = null;
  /** Whether options have been fetched at least once (to distinguish "not yet loaded" from "empty results"). */
  dynamicOptionsFetched = false;

  /** Cached references to resolved parent controls (may live in a different FormGroup). */
  private parentControls = new Map<string, AbstractControl>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly provisionState: DeploymentProvisionStateService,
    private readonly fleetConfigService: FleetConfigurationService,
  ) {}

  /** Wrapper to expose InputType to HTML template. */
  public get InputType(): typeof InputType { return InputType; }

  /** Wrapper to expose InputMode to HTML template. */
  public get InputMode(): typeof InputMode { return InputMode; }

  /** The current form control instance queried from the parent form group. */
  get formControl(): FormControl { return this.form.get(this.controlMetadata.name) as FormControl; }

  /** Returns true if the required validator was added to the control. */
  get required(): boolean { return this.formControl.hasValidator(Validators.required); }

  /** Returns true if this control uses dynamic options. */
  get isDynamic(): boolean { return this.controlMetadata.optionsSource === 'dynamic'; }

  ngOnInit(): void {
    if (this.isDynamic) {
      this.initDynamicOptions();
    }
    if (this.controlMetadata.userInputType === InputType.IncadaTenantIdDropdown) {
      this.syncIncadaTenantId();
    }
    if (this.controlMetadata.userInputType === InputType.IncadaTenantSlugDropdown) {
      this.syncIncadaTenantSlug();
    }
    if (this.controlMetadata.userInputType === InputType.IncadaRegionDropdown) {
      this.syncIncadaRegion();
    }
    if (this.controlMetadata.userInputType === InputType.IncadaLocationSlugDropdown) {
      this.syncIncadaLocationSlug();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedOrganization'] && this.controlMetadata?.userInputType === InputType.IncadaTenantIdDropdown) {
      this.syncIncadaTenantId();
    }
    if (changes['selectedOrganization'] && this.controlMetadata?.userInputType === InputType.IncadaTenantSlugDropdown) {
      this.syncIncadaTenantSlug();
    }
    if (changes['selectedOrganization'] && this.controlMetadata?.userInputType === InputType.IncadaRegionDropdown) {
      this.syncIncadaRegion();
    }
    if (changes['selectedSite'] && this.controlMetadata?.userInputType === InputType.IncadaLocationSlugDropdown) {
      this.syncIncadaLocationSlug();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Handler for provision status updates emitted by system select child component.
   * Refactored: delegates provisioning status tracking to DeploymentProvisionStateService
   * instead of mutating hidden form controls locally. */
  onProvisionStatusChange(status: { systemId: string; isProvisioned: boolean; isConflict: boolean }): void {
    if (this.controlMetadata.userInputType !== InputType.SystemDropdown) return;
    this.provisionState.update({
      systemId: status.systemId,
      isProvisioned: status.isProvisioned,
      isConflict: status.isConflict,
      warningApplies: status.isProvisioned && !status.isConflict
    });
  }

  /**
   * Handles selection from a dynamic options dropdown.
   * Stores the value in "label||value" combined format.
   */
  onDynamicOptionSelected(selectedValue: string): void {
    // The mat-select value is already set to the "label||value" combined string.
    // Clear stale warning since user made a new selection.
    this.staleOptionLabel = null;
  }

  /**
   * Syncs the IncadaTenantIdDropdown form control value with the selected organization's incadaId.
   * The control is always disabled/read-only - its value is derived entirely from the org selection.
   */
  private syncIncadaTenantId(): void {
    const tenantId = this.selectedOrganization?.incadaId || '';
    if (this.formControl) {
      this.formControl.setValue(tenantId);
      this.formControl.disable();
    }
  }

  /**
   * Syncs the IncadaTenantSlugDropdown form control value with the selected organization's incadaSlug.
   * The control is always disabled/read-only — its value is derived entirely from the org selection.
   */
  private syncIncadaTenantSlug(): void {
    const slug = this.selectedOrganization?.incadaSlug || '';
    if (this.formControl) {
      this.formControl.setValue(slug);
      this.formControl.disable();
    }
  }

  /**
   * Syncs the IncadaRegionDropdown form control value with the selected organization's incadaRegion.
   * The control is always disabled/read-only — its value is derived entirely from the org selection.
   */
  private syncIncadaRegion(): void {
    const regionSlug = this.selectedOrganization?.incadaRegion || '';
    if (this.formControl) {
      this.formControl.setValue(regionSlug);
      this.formControl.disable();
    }
  }

  /**
   * Syncs the IncadaLocationSlugDropdown form control value with the selected site's incadaSlug.
   * The control stays enabled (so Angular includes it in form validation) but the template
   * renders it as readonly. If a site is selected but has no incadaSlug, a validation error
   * is set to block form submission.
   */
  private syncIncadaLocationSlug(): void {
    if (!this.formControl) return;

    const siteSelected = !!this.selectedSite;
    const slug = this.selectedSite?.incadaSlug || '';

    this.formControl.setValue(slug);

    if (siteSelected && !slug) {
      // Site chosen but not Incada-enabled — mark invalid.
      this.setIncadaEnabledError(true);
      this.formControl.markAsTouched();
    } else {
      // No site selected or valid slug — clear only Incada-specific error.
      this.setIncadaEnabledError(false);
    }
  }

  /** Adds or removes only the notIncadaEnabled error while preserving all other validator errors. */
  private setIncadaEnabledError(hasError: boolean): void {
    const currentErrors = this.formControl.errors || {};

    if (hasError) {
      this.formControl.setErrors({ ...currentErrors, notIncadaEnabled: true });
      return;
    }

    if (!currentErrors['notIncadaEnabled']) {
      return;
    }

    const { notIncadaEnabled, ...remainingErrors } = currentErrors;
    this.formControl.setErrors(Object.keys(remainingErrors).length > 0 ? remainingErrors : null);
  }

  /** Called by the template when the child site-select emits a selected site. Re-emits it upward. */
  onSiteSelectedFromChild(site: Organization | null): void {
    this.siteSelected.emit(site);
  }

  // ── Dynamic options private helpers ───────────────────────────────────

  /** Sets up watchers on parent controls listed in dependsOn and triggers option fetches. */
  private initDynamicOptions(): void {
    const dependsOn = this.controlMetadata.dependsOn || [];

    if (dependsOn.length === 0) {
      // No dependencies — fetch immediately.
      this.fetchDynamicOptions();
      return;
    }

    // Resolve and cache parent controls — they may live in a different FormGroup
    // (e.g. deployment-level property referenced from a system/feature-level control).
    dependsOn.forEach(parentName => {
      const resolved = this.resolveParentControl(parentName);
      if (resolved) {
        this.parentControls.set(parentName, resolved);
      } else {
        console.warn(`[DynamicDeploymentControl] dependsOn parent '${parentName}' not found anywhere in the form tree.`);
      }
    });

    // Build initial placeholder based on friendly names of parent controls if available.
    this.dynamicPlaceholder = this.buildDependsOnPlaceholder();

    // Watch each resolved parent control for value changes.
    this.parentControls.forEach((parentControl, parentName) => {
      parentControl.valueChanges.pipe(
        debounceTime(300),
        takeUntil(this.destroy$),
      ).subscribe(() => {
        this.onParentValueChanged();
      });
    });

    // Check if all parent values are already filled (e.g. re-edit scenario).
    if (this.allParentsFilled()) {
      this.fetchDynamicOptions();
    }
  }

  /** Called whenever any parent control's value changes. */
  private onParentValueChanged(): void {
    // Clear current selection and options.
    this.formControl.setValue(null);
    this.dynamicOptions = [];
    this.dynamicOptionsFetched = false;
    this.staleOptionLabel = null;

    if (this.allParentsFilled()) {
      this.fetchDynamicOptions();
    } else {
      this.dynamicPlaceholder = this.buildDependsOnPlaceholder();
    }
  }

  /** Returns true if all dependsOn parent controls have a truthy value. */
  private allParentsFilled(): boolean {
    const dependsOn = this.controlMetadata.dependsOn || [];
    return dependsOn.every(parentName => {
      const parentControl = this.parentControls.get(parentName);
      return parentControl && parentControl.value != null && parentControl.value !== '';
    });
  }

  /** Fetches options from the dynamic endpoint, passing parent values as query params. */
  private fetchDynamicOptions(): void {
    const endpoint = this.controlMetadata.optionsEndpoint;
    if (!endpoint) {
      console.error(`[DynamicDeploymentControl] optionsEndpoint is missing for dynamic control '${this.controlMetadata.name}'.`);
      return;
    }

    // Collect parent values from cached references.
    const parentValues: Record<string, string> = {};
    (this.controlMetadata.dependsOn || []).forEach(parentName => {
      const parentControl = this.parentControls.get(parentName);
      if (parentControl) {
        parentValues[parentName] = parentControl.value;
      }
    });

    this.dynamicOptionsLoading = true;
    this.dynamicPlaceholder = 'Loading options…';

    // Save the current form control value before fetch (for re-edit stale detection).
    const previousValue = this.formControl.value;

    this.fleetConfigService.getDynamicPropertyOptions(endpoint, parentValues)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (options) => {
          this.dynamicOptions = options || [];
          this.dynamicOptionsLoading = false;
          this.dynamicOptionsFetched = true;

          if (this.dynamicOptions.length === 0) {
            this.dynamicPlaceholder = `No options found`;
          } else {
            this.dynamicPlaceholder = '';
          }

          // Handle re-edit: if there's a previously saved "label||value", try to match it.
          this.reconcilePreviousValue(previousValue);
        },
        error: (err) => {
          console.error(`[DynamicDeploymentControl] Error fetching dynamic options for '${this.controlMetadata.name}':`, err);
          this.dynamicOptionsLoading = false;
          this.dynamicOptionsFetched = true;
          this.dynamicPlaceholder = 'Error loading options';
        }
      });
  }

  /**
   * If the control already has a saved value (re-edit flow), try to match it against the
   * freshly fetched options. If no match, show a stale warning.
   */
  private reconcilePreviousValue(previousValue: string | null): void {
    if (!previousValue) return;

    // The saved value uses "label||value" format.
    const parts = previousValue.split('||');
    const savedLabel = parts[0];
    const savedValue = parts.length > 1 ? parts[1] : null;

    if (savedValue) {
      // Try to find the option in the fresh list by its underlying value.
      const existingOption = this.dynamicOptions.find(opt => opt.value === savedValue);
      if (existingOption) {
        // Option still exists — set the combined value on the control.
        this.formControl.setValue(`${existingOption.label}||${existingOption.value}`);
        this.staleOptionLabel = null;
      } else {
        // Option no longer exists — flag as stale.
        this.staleOptionLabel = savedLabel;
        this.formControl.setValue(null);
      }
    } else {
      // Legacy value without || separator — try matching by label as a display name.
      const existingOption = this.dynamicOptions.find(opt => opt.label === savedLabel);
      if (existingOption) {
        this.formControl.setValue(`${existingOption.label}||${existingOption.value}`);
        this.staleOptionLabel = null;
      } else if (this.dynamicOptions.length > 0) {
        this.staleOptionLabel = savedLabel;
        this.formControl.setValue(null);
      }
    }
  }

  /** Builds a user-friendly placeholder like "Select a {ParentName} first". */
  private buildDependsOnPlaceholder(): string {
    const dependsOn = this.controlMetadata.dependsOn || [];
    const missingParents = dependsOn.filter(parentName => {
      const parentControl = this.parentControls.get(parentName);
      return !parentControl || parentControl.value == null || parentControl.value === '';
    });

    if (missingParents.length === 0) return '';
    if (missingParents.length === 1) return `Select ${missingParents[0]} first`;
    return `Fill in ${missingParents.join(', ')} first`;
  }

  // ── Form tree traversal ───────────────────────────────────────────────

  /**
   * Resolves a parent control by name. Checks the immediate form group first,
   * then walks the entire form tree from the root so cross-level dependsOn
   * references (e.g. deployment-level → feature-level) are found.
   */
  private resolveParentControl(name: string): AbstractControl | null {
    // Fast path: sibling in the same FormGroup.
    const direct = this.form.get(name);
    if (direct) return direct;

    // Slow path: search from the root of the reactive form tree.
    return this.findControlInTree(this.form.root, name);
  }

  /** Recursively searches a form tree (FormGroup / FormArray) for a control with the given name. */
  private findControlInTree(node: AbstractControl, name: string): AbstractControl | null {
    if (node instanceof FormGroup) {
      // Use get() instead of contains() because contains() returns false for disabled controls,
      // which would cause lookups of the IncadaTenantSlugDropdown (which is currently always disabled by design) to fail.
      const ctrl = node.get(name);
      if (ctrl) return ctrl;
      for (const key of Object.keys(node.controls)) {
        const found = this.findControlInTree(node.controls[key], name);
        if (found) return found;
      }
    }
    if (node instanceof FormArray) {
      for (let i = 0; i < node.length; i++) {
        const found = this.findControlInTree(node.at(i), name);
        if (found) return found;
      }
    }
    return null;
  }
}
